/**
 * @fileoverview Shared Enums - Centralized enum definitions
 * @description Common enums used across all domain schemas
 * @version 0.18.4
 */

import { z } from 'zod';

// === AUTHENTICATION ENUMS ===
export const RoleNameSchema = z.enum(['owner', 'admin', 'ml_developer', 'client']);
export const PermissionNameSchema = z.string().min(1);
export const SessionStatusSchema = z.enum(['active', 'expired', 'revoked']);
export const AuthLevelSchema = z.enum(['public', 'user', 'service', 'admin']);

// === ML ENUMS ===
export const SignalStrengthSchema = z.enum(['weak', 'moderate', 'strong']);
export const SignalDirectionSchema = z.enum(['bullish', 'bearish', 'neutral']);
export const SignalTypeSchema = z.enum(['entry', 'exit', 'scalping', 'swing']);
export const ConsensusDecisionSchema = z.enum(['buy', 'sell', 'hold']);
export const ModelTypeSchema = z.enum(['lstm', 'random_forest', 'xgboost', 'ensemble', 'transformer']);
export const ModelStatusSchema = z.enum(['active', 'inactive', 'training', 'deprecated']);

// === TRADING ENUMS ===
export const TradeSideSchema = z.enum(['BUY', 'SELL']);
export const OrderTypeSchema = z.enum(['market', 'limit', 'stop', 'stop_limit']);
export const OrderStatusSchema = z.enum(['pending', 'filled', 'cancelled', 'rejected', 'expired']);
export const PositionStatusSchema = z.enum(['open', 'closed', 'liquidated']);

// === PORTFOLIO ENUMS ===
export const AccountTypeSchema = z.enum(['ISA', 'SIPP', 'GIA', 'CFD', 'SPREAD_BET']);

// === MARKET ENUMS ===
export const TimeframeSchema = z.enum(['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1M']);
export const DataProviderSchema = z.enum(['ig', 'hl', 'yahoo', 'alpha_vantage', 'twelve_data']);

// === JOB ENUMS ===
export const JobStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'cancelled']);
export const JobPrioritySchema = z.enum(['low', 'normal', 'high', 'critical']);
export const JobTypeSchema = z.enum(['data_ingestion', 'ml_training', 'signal_generation', 'portfolio_sync']);

// === STREAMING ENUMS ===
export const StreamEventTypeSchema = z.enum(['position_update', 'signal_update', 'price_update', 'job_update']);

// === PAGINATION ENUMS ===
export const SortDirectionSchema = z.enum(['asc', 'desc']);
export const FilterOperatorSchema = z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'like', 'in', 'nin']);

// === DEPRECATION ENUMS ===
export const DeprecationStatusSchema = z.enum(['active', 'deprecated', 'removed']);
export const DeprecationStatusValueSchema = z.enum(['dual_support', 'deprecation_warnings', 'legacy_removed']);

// === MARKETPLACE ENUMS ===
export const VendorStatusSchema = z.enum(['pending', 'approved', 'suspended']);
export const ModelListingStatusSchema = z.enum(['draft', 'published', 'deprecated']);
export const DeploymentStatusSchema = z.enum(['deploying', 'active', 'scaling', 'stopped', 'failed']);

// === COMMON ENUMS ===
export const HttpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']);
export const PerformanceSLASchema = z.enum(['fast', 'standard', 'slow', 'batch']);

// Type exports for all enums
export type RoleName = z.infer<typeof RoleNameSchema>;
export type PermissionName = z.infer<typeof PermissionNameSchema>;
export type SessionStatus = z.infer<typeof SessionStatusSchema>;
export type AuthLevel = z.infer<typeof AuthLevelSchema>;

export type SignalStrength = z.infer<typeof SignalStrengthSchema>;
export type SignalDirection = z.infer<typeof SignalDirectionSchema>;
export type SignalType = z.infer<typeof SignalTypeSchema>;
export type ConsensusDecision = z.infer<typeof ConsensusDecisionSchema>;
export type ModelType = z.infer<typeof ModelTypeSchema>;
export type ModelStatus = z.infer<typeof ModelStatusSchema>;

export type TradeSide = z.infer<typeof TradeSideSchema>;
export type OrderType = z.infer<typeof OrderTypeSchema>;
export type OrderStatus = z.infer<typeof OrderStatusSchema>;
export type PositionStatus = z.infer<typeof PositionStatusSchema>;

export type AccountType = z.infer<typeof AccountTypeSchema>;

export type Timeframe = z.infer<typeof TimeframeSchema>;
export type DataProvider = z.infer<typeof DataProviderSchema>;

export type JobStatus = z.infer<typeof JobStatusSchema>;
export type JobPriority = z.infer<typeof JobPrioritySchema>;
export type JobType = z.infer<typeof JobTypeSchema>;

export type StreamEventType = z.infer<typeof StreamEventTypeSchema>;

export type SortDirection = z.infer<typeof SortDirectionSchema>;
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;

export type DeprecationStatus = z.infer<typeof DeprecationStatusSchema>;
export type DeprecationStatusValue = z.infer<typeof DeprecationStatusValueSchema>;

export type VendorStatus = z.infer<typeof VendorStatusSchema>;
export type ModelListingStatus = z.infer<typeof ModelListingStatusSchema>;
export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>;

export type HttpMethod = z.infer<typeof HttpMethodSchema>;
export type PerformanceSLA = z.infer<typeof PerformanceSLASchema>;
